import { useState, type ReactNode } from 'react';
import clsx from 'clsx';

export interface Column<T> {
  key: string;
  header: string;
  sortable?: boolean;
  sortValue?: (row: T) => string | number;
  className?: string;
  render: (row: T, index: number) => ReactNode;
}

interface TableProps<T> {
  columns: Column<T>[];
  data: T[];
  loading?: boolean;
  emptyMessage?: string;
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  /** Optional controlled sort (e.g. so a CSV export can follow the on-screen order). */
  sort?: SortState | null;
  onSortChange?: (sort: SortState) => void;
}

export type SortDir = 'asc' | 'desc';
export type SortState = { key: string; dir: SortDir };

/** Sort rows the way the table does for a given column. */
export function sortRows<T>(data: T[], columns: Column<T>[], sort: SortState | null | undefined): T[] {
  if (!sort) return data;
  const col = columns.find((c) => c.key === sort.key);
  if (!col?.sortValue) return data;
  return [...data].sort((a, b) => {
    const av = col.sortValue!(a);
    const bv = col.sortValue!(b);
    const cmp = typeof av === 'number' && typeof bv === 'number'
      ? av - bv
      : String(av).localeCompare(String(bv), undefined, { sensitivity: 'base' });
    return sort.dir === 'asc' ? cmp : -cmp;
  });
}

function SortIcon({ active, dir }: { active: boolean; dir: SortDir }) {
  return (
    <svg
      className={clsx('ml-1 h-3.5 w-3.5 flex-shrink-0', active ? 'text-gray-700' : 'text-gray-300')}
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden="true"
    >
      {active && dir === 'asc' ? (
        <path fillRule="evenodd" d="M10 3a.75.75 0 01.55.24l3.25 3.5a.75.75 0 11-1.1 1.02L10 4.852 7.3 7.76a.75.75 0 01-1.1-1.02l3.25-3.5A.75.75 0 0110 3zm-3.76 9.24a.75.75 0 011.06-.04L10 14.648l2.7-2.448a.75.75 0 111.02 1.1l-3.25 3a.75.75 0 01-1.02 0l-3.25-3a.75.75 0 01-.04-1.06z" clipRule="evenodd" />
      ) : active && dir === 'desc' ? (
        <path fillRule="evenodd" d="M10 17a.75.75 0 01-.55-.24l-3.25-3.5a.75.75 0 111.1-1.02L10 15.148l2.7-2.908a.75.75 0 111.1 1.02l-3.25 3.5A.75.75 0 0110 17zm3.76-9.24a.75.75 0 00-1.06.04L10 10.352 7.3 7.904a.75.75 0 00-1.02 1.1l3.25 3a.75.75 0 001.02 0l3.25-3a.75.75 0 00.04-1.06z" clipRule="evenodd" />
      ) : (
        <path fillRule="evenodd" d="M10 3a.75.75 0 01.55.24l3.25 3.5a.75.75 0 11-1.1 1.02L10 4.852 7.3 7.76a.75.75 0 01-1.1-1.02l3.25-3.5A.75.75 0 0110 3zm0 14a.75.75 0 01-.55-.24l-3.25-3.5a.75.75 0 111.1-1.02L10 15.148l2.7-2.908a.75.75 0 111.1 1.02l-3.25 3.5A.75.75 0 0110 17z" clipRule="evenodd" />
      )}
    </svg>
  );
}

export default function Table<T>({
  columns,
  data,
  loading,
  emptyMessage = 'No data.',
  rowKey,
  onRowClick,
  sort,
  onSortChange,
}: TableProps<T>) {
  const [internalSort, setInternalSort] = useState<SortState | null>(null);
  const controlled = onSortChange !== undefined;
  const current = controlled ? sort ?? null : internalSort;
  const sortKey = current?.key ?? null;
  const sortDir: SortDir = current?.dir ?? 'asc';

  const handleSort = (key: string) => {
    const next: SortState =
      sortKey === key ? { key, dir: sortDir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' };
    if (controlled) onSortChange(next);
    else setInternalSort(next);
  };

  const sortedData = sortRows(data, columns, current);

  return (
    <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead className="bg-gray-50">
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                scope="col"
                className={clsx(
                  'px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500',
                  col.sortable && 'cursor-pointer select-none hover:text-gray-700',
                  col.className,
                )}
                onClick={col.sortable ? () => handleSort(col.key) : undefined}
                aria-sort={
                  sortKey === col.key
                    ? sortDir === 'asc' ? 'ascending' : 'descending'
                    : undefined
                }
              >
                <span className="inline-flex items-center">
                  {col.header}
                  {col.sortable && (
                    <SortIcon active={sortKey === col.key} dir={sortDir} />
                  )}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {loading
            ? Array.from({ length: 5 }).map((_, i) => (
                <tr key={i}>
                  {columns.map((col) => (
                    <td key={col.key} className="px-4 py-3">
                      <div className="h-4 animate-pulse rounded bg-gray-100" />
                    </td>
                  ))}
                </tr>
              ))
            : sortedData.length === 0
              ? (
                <tr>
                  <td colSpan={columns.length} className="px-4 py-8 text-center text-sm text-gray-500">
                    {emptyMessage}
                  </td>
                </tr>
              )
              : sortedData.map((row, idx) => (
                <tr
                  key={rowKey(row)}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  className={clsx(
                    'transition-colors',
                    onRowClick && 'cursor-pointer hover:bg-gray-50',
                  )}
                >
                  {columns.map((col) => (
                    <td key={col.key} className={clsx('px-4 py-3 text-gray-700', col.className)}>
                      {col.render(row, idx)}
                    </td>
                  ))}
                </tr>
              ))}
        </tbody>
      </table>
    </div>
  );
}
